// Frequently asked questions.
//
// Ported from app/views/scholarship/faq.haml. The original toggled each answer with an inline
// jQuery onclick; these use <details>, which does the same thing natively and works with a
// keyboard and a screen reader.
//
// Two answers deliberately differ from the original, both noted where they appear.

import { Link } from 'react-router-dom';
import { useSession } from '../App.tsx';
import { Loading } from '../components/Bits.tsx';

interface Question {
  q: string;
  a: React.ReactNode;
}

const MAILING_ADDRESS = (
  <>
    Nupur Gupta<br />
    SEED Foundation Scholarship Program<br />
    2500 Independence Ave<br />
    Glenview, IL 60026
  </>
);

function questions(year: number): Question[] {
  return [
    {
      q: 'When will the scholarship winners be announced?',
      a: <>Winners are announced in the summer of {year} and posted on The SEED Foundation website.</>,
    },
    {
      q: 'I already have a scholarship that covers tuition. Can I use this for room and board?',
      a: <>No. The SEED Scholarship can only be applied to college tuition.</>,
    },
    {
      q: 'Will all applicants be told who received the scholarship?',
      a: <>The winners are posted on our website.</>,
    },
    {
      q: 'Where do I send my transcript, recommendation and other documents?',
      a: (
        <>
          Send everything in <strong>one package</strong> to:
          <p style={{ marginBottom: 0 }}>{MAILING_ADDRESS}</p>
        </>
      ),
    },
    {
      q: 'Can I send some documents separately?',
      a: (
        <>
          No. Everything must arrive in one package — an application whose documents arrive
          separately is disqualified.
        </>
      ),
    },
    {
      q: 'Should I use certified mail?',
      a: (
        <>
          No. Do not use certified mail, or any service that requires a signature. We cannot
          accept signature-required mail, and an application sent that way is disqualified.
        </>
      ),
    },
    {
      q: 'Does my documentation need to arrive by the deadline, or just be postmarked by then?',
      a: <>It must be <strong>postmarked</strong> by April 30th.</>,
    },
    {
      q: 'Am I eligible if only one of my parents is of Indian descent?',
      a: <>Yes. You are eligible if at least one parent is of Indian origin.</>,
    },
    {
      q: 'Am I eligible if both of my parents are of Indian descent?',
      a: <>Yes.</>,
    },
    {
      q: 'I am not a U.S. citizen but I am a legal resident. Can I still apply?',
      a: <>Yes. Legal residents of the United States are eligible.</>,
    },
    {
      q: 'Taxes are due April 15. What if my parents have not filed in time?',
      a: (
        <>
          All information, including the {year - 2} tax return, must be submitted by April 30th.
        </>
      ),
    },
    {
      // The original answered this with "we require these forms... including your SSN". The
      // application no longer asks for a Social Security number at all, so the old answer
      // would now be describing something that does not happen.
      q: 'Why do you need financial information?',
      a: (
        <>
          This is a need-based scholarship, so we ask for household income and how many
          siblings are in college. That is all the application asks for — we do not collect
          Social Security numbers. Supporting documents you post to us are kept confidential
          and destroyed once the scholarships have been awarded.
        </>
      ),
    },
  ];
}

export function Faq() {
  const { data, isPending } = useSession();
  if (isPending) return <main id="main"><Loading /></main>;

  const year = data?.cycle?.year ?? new Date().getUTCFullYear() + 1;

  return (
    <main id="main">
      <p><Link to="/">← Back</Link></p>
      <h2>Frequently asked questions</h2>
      <p className="lede">
        Please look here before contacting us — most questions are answered below.
      </p>

      <div className="panel">
        {questions(year).map((item, index) => (
          <details key={index} style={{ borderBottom: '1px solid var(--line)', padding: '0.7rem 0' }}>
            <summary style={{ cursor: 'pointer', fontWeight: 600 }}>{item.q}</summary>
            <div style={{ marginTop: '0.6rem', color: 'var(--ink-soft)' }}>{item.a}</div>
          </details>
        ))}
      </div>

      <h3>Still stuck?</h3>
      <div className="panel">
        <p style={{ marginTop: 0 }}>
          <a href="mailto:scholarship@seedfoundation.org">scholarship@seedfoundation.org</a>
        </p>
        <p style={{ marginBottom: 0 }}>{MAILING_ADDRESS}</p>
      </div>
    </main>
  );
}
